import React, { useState } from 'react'
import { CredentialCardProps } from "./CredentialCard.props";
import { Box, Stack, styled, Tooltip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import './CredentialCard.styles.css';
import { AlertMessage } from "../../atoms/AlertMessage";
import { SyncedStatus } from "../../atoms/SyncedStatus";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import { Modal } from "../../atoms/Modal";
import moment from "moment-timezone";
import { ACCOUNT_DATA_KEYS } from "../../../constants/contants";

const BoxWrapper = styled('div')(() => ({
    width: '50%',
    maxWidth: '700px',
    fontSize: '14px'
}));

const DivWrapper = styled('div')(({ theme }) => ({
    borderStyle: 'solid',
    borderColor: theme.palette.border.main,
    borderRadius: theme.shape.borderRadius,
    borderWidth: '1px',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
}));


const SepLine = styled('div')(({ theme }) => ({
    borderStyle: 'solid',
    borderTopColor: theme.palette.border.main,
    borderWidth: '0px',
    borderTopWidth: '1px',
    width: "100%",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const OutlineButton = styled(Button)(({ theme }) => ({
    color: '#345C8B',
    borderColor: '#CCCFCF',
    minHeight: "40px",
     "&:hover": {
        borderColor: theme.palette.border.main
    },
}));

const PrimaryButton = styled(Button)(() => ({
    backgroundColor: '#345C8B',
    color: "white",
    borderColor: '#CCCFCF',
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
    backgroundColor: 'white',
    border: '1px solid #CCCFCF',
    "&:hover": {
        backgroundColor: theme.palette.border.main
    },
}));

const serviceMessages = (service_name:any) => {
    if(service_name === ACCOUNT_DATA_KEYS.AUTO_BLOCK_LIST || service_name === ACCOUNT_DATA_KEYS.BLOCK_LIST){
        return <>
            When you remove, your Microsoft 365 account will be logged out. Are you sure you want to remove your account from Block List?
            <Box className={'info-box'} sx={{marginTop: 2}}>
                <p>
                    Block List feature enabled will be <strong>disabled</strong>.
                </p>
                <p><strong>If this feature is the last one connected to the tenant</strong>, disabling
                    it <strong>disconnects</strong> the tenant
                </p>
            </Box>
        </>
    } else if(service_name === ACCOUNT_DATA_KEYS.AUTO_PHISH_CLEAR || service_name === ACCOUNT_DATA_KEYS.PHISH_CLEAR){
        return <>
            When you remove, your Microsoft 365 account will be logged out. Are you sure you want to remove your account from Phish Clear?
            <Box className={'info-box'} sx={{marginTop: 2}}>
                <p>
                    Phish Clear feature enabled will be <strong>disabled</strong>.
                </p>
                <p><strong>If this feature is the last one connected to the tenant</strong>, disabling
                    it <strong>disconnects</strong> the tenant
                </p>
            </Box>
        </>
    }
}

export const CredentialCard: React.FC<CredentialCardProps> = ({
    payload = {
        service_key: "",
        service_id: ACCOUNT_DATA_KEYS.AUTO_BLOCK_LIST,
        category: undefined,
        authenticated: false,
        loggedTitle: "True",
        notLoggedTitle: "true",
        accountName: "",
        accountEmail: "",
        loginAt: "",
        daysRemaining: 0,
        bodyContent: "",
        disableBtn: false,
        expiredBannerOnly: false,
    },
    onRemoveCredential = () => {},
    onLogIn = () => {},
}) => {
    const { t } = useTranslation();
    const theme = useTheme()
    const [showMessage, setShowMessage] = useState(false)

    const serviceMessage = serviceMessages(payload['service_id'])
    //@ts-ignore
    const tz_name = JSON.parse(localStorage.getItem('userData'))["tz_name"] || 'Etc/UTC';

    const getCredentialStatusAlert = () => {
        const dateFormat = 'DD/MM/YYYY';
        const loginMoment = moment.unix(payload["loginAt"]).utc().tz(tz_name);
        const expiryMoment = moment.unix(payload["expireAt"]).utc().tz(tz_name);
        const daysRemaining = payload["daysRemaining"] ?? '';
        const serviceTitle = payload["service_key"] === ACCOUNT_DATA_KEYS.BLOCK_LIST ? "Block List" : payload["service_key"] === ACCOUNT_DATA_KEYS.PHISH_CLEAR ? "Phish Clear" : ""
        const isAutomation = payload["service_id"] === ACCOUNT_DATA_KEYS.AUTO_BLOCK_LIST || payload["service_id"] === ACCOUNT_DATA_KEYS.AUTO_PHISH_CLEAR;

        const baseResults = {
            title: 're_login_title',
            serviceTitle: serviceTitle,
            content: 'login_on_message',
            date: loginMoment.format(dateFormat),
            days: 0,
            severity: 'info',
            expireTime: '',
        };

        if (daysRemaining === '') return baseResults;
        // Already expired case
        if (daysRemaining <= 0) {
            return {
                ...baseResults,
                title: 'expired_title',
                content: `expired_${isAutomation ? 'automation' : 'manual'}_message`,
                date: expiryMoment.format(dateFormat),
                severity: 'error',
            };
        }

        // Near expiry case
        if (daysRemaining <= 15) {
            const currentMoment = moment.utc().tz(tz_name);
            const isExpiringToday = expiryMoment.isSame(currentMoment, 'day');
            return {
                ...baseResults,
                title: isExpiringToday ? 'expiring_today_title' : 'near_expiry_title',
                content: isExpiringToday ? 'expiring_today_message' :  'near_expiry_message',
                date: expiryMoment.format(dateFormat),
                days: Math.round(payload["daysRemaining"]),
                expireTime: expiryMoment.format('hh:mm A'),
                severity: 'warning',
            };
        }
        // Default case (not expired, not near expiry)
        return baseResults;
    }

    const onClickLogin = (isReLogin = false) => {
        onLogIn(isReLogin)
    }

    const onClickRemoveCredential = () => {
        setShowMessage(false)
        onRemoveCredential();
    }

    return <>
        {!payload?.['expiredBannerOnly'] || payload?.['daysRemaining'] > 0 ?
          <BoxWrapper className={'credential-card-wrapper'}>
              <DivWrapper>
                  <div className="credential-card-title">
                      <Box className="credential-card-title-left" sx={{ fontSize: "16px" }}>
                          <div dangerouslySetInnerHTML={{ __html: payload['title'] }}></div>
                      </Box>
                      <div className="credential-card-title-right">
                          <Tooltip
                            placement={"top"} title={t("credentialCard.connected_tenant_tooltip")}
                            componentsProps={{ tooltip: { sx: { maxWidth: "450px" } } }}
                          >
                              <SyncedStatus
                                value={(payload['authenticated'] && payload['daysRemaining'] > 0) ? "Connected to MS 365 tenant " : "Not connected to MS 365 tenant"}
                                icon={(payload['authenticated'] && payload['daysRemaining'] > 0) ?
                                  <CheckCircleIcon/> : <TimerOffOutlinedIcon htmlColor={'#434F4F'}/>}
                                valueColor={(payload['authenticated'] && payload['daysRemaining'] > 0) ? "#008545" : "#434F4F"}/>
                          </Tooltip>
                      </div>
                  </div>
                  <SepLine/>
                  <div className="credential-card-body">
                      <div className="credential-card-body">
                          {
                              payload['authenticated'] ?
                                <div>
                                    <AlertMessage content={
                                        <>
                                            <Typography sx={{
                                                marginBottom: theme.spacing(1),
                                                fontSize: "14px",
                                                fontWeight: "700",
                                                textTransform: 'uppercase'
                                            }}>
                                                {t(`credentialCard.${getCredentialStatusAlert().title}`, {
                                                    days: getCredentialStatusAlert().days,
                                                })}
                                            </Typography>
                                            <Typography variant="body2"
                                                        sx={{ marginBottom: theme.spacing(1), fontWeight: "500" }}>
                                                <div
                                                  dangerouslySetInnerHTML={{
                                                      __html: t(`credentialCard.${getCredentialStatusAlert().content}`,
                                                        {
                                                            date: getCredentialStatusAlert().date,
                                                            expireTime: getCredentialStatusAlert().expireTime,
                                                            serviceTitle: getCredentialStatusAlert().serviceTitle,
                                                        })
                                                  }}>
                                                </div>
                                            </Typography>
                                        </>
                                    } severity={getCredentialStatusAlert().severity} roundWarningIcon/>
                                    {getCredentialStatusAlert().severity !== 'error' && <>
                                        <Box sx={{ marginBottom: '8px' }}>
                                            {t("credentialCard.login_as")}:
                                        </Box>
                                        <Box sx={{ marginBottom: '16px', fontWeight: 700 }}>
                                            <i>{payload['accountEmail']} ({payload['accountName']})</i>
                                        </Box>
                                        <Typography sx={{
                                            marginBottom: '16px',
                                            fontSize: '12px',
                                            color: '#666666',
                                            fontWeight: 500
                                        }}>
                                            <div
                                              dangerouslySetInnerHTML={{ __html: t(`credentialCard.note_message_${payload["service_id"]}${payload["expiredBannerOnly"] ? '_rule_details' : ''}`) }}></div>
                                        </Typography>
                                    </>}
                                    {getCredentialStatusAlert().severity === 'warning' &&
                                      <PrimaryButton data-testid={'re_login'} variant="contained"
                                                     onClick={() => onClickLogin(true)}>
                                          {t("credentialCard.re_login")}
                                      </PrimaryButton>}
                                    {getCredentialStatusAlert().severity === 'error' &&
                                      <Stack direction="row" gap={2}>
                                          <PrimaryButton data-testid={'common_expired_login'} variant={"contained"}
                                                         onClick={() => onClickLogin(true)}>
                                              {t("common.login")}
                                          </PrimaryButton>
                                          <OutlineButton data-testid={'dismiss_expired'} variant="outlined"
                                                         onClick={() => onClickRemoveCredential()}>
                                              {t("common.dismiss")}
                                          </OutlineButton>
                                      </Stack>}
                                    {(!payload['disableBtn'] && getCredentialStatusAlert().severity !== 'error') &&
                                      <OutlineButton data-testid={'remove_credential'} variant="outlined"
                                                     onClick={() => {
                                                         setShowMessage(true)
                                                     }}>
                                          {t("credentialCard.remove_credential")}
                                      </OutlineButton>}
                                </div>
                                :
                                <div>
                                    <Box sx={{ marginBottom: '16px' }}>
                                        <div
                                          dangerouslySetInnerHTML={{ __html: t("credentialCard.unauthenticated_" + payload['service_id']) }}></div>
                                    </Box>
                                    {!payload['disableBtn'] &&
                                      <PrimaryButton data-testid={'common_login'} variant={"contained"}
                                                     onClick={() => onClickLogin()}>
                                          {t("common.login")}
                                      </PrimaryButton>}
                                </div>
                          }
                      </div>
                  </div>
              </DivWrapper>
              <Modal idModal="modal-setting-off-click"
                     data-testid={'modal-setting-off-click'}
                     openDialog={showMessage}
                     variant={"delete"}
                     title={"Remove account"}
                     actionAgree={() => onClickRemoveCredential()}
                     actionCancel={() => setShowMessage(false)}
                     textAgree={"Save changes & Remove"}
                     onClose={() => setShowMessage(false)}
                     children={serviceMessage}
              />
          </BoxWrapper> : <Stack className="expired-banner-small" gap={2}>
              <div>Requires <b>Microsoft 365 credentials.</b></div>
              <AlertMessage content={
                  <Typography variant="body2" sx={{ marginBottom: theme.spacing(1), fontWeight: "500" }}>
                      <Stack direction="row" gap={1}>
                          <div id="small-banner-title">{t("credentialCard.expired_banner_only_message", { date: getCredentialStatusAlert().date, serviceTitle: getCredentialStatusAlert().serviceTitle })}</div>
                          <SecondaryButton className="small-banner-btn" onClick={() => onClickRemoveCredential()}>{t("common.dismiss")}</SecondaryButton>
                          <SecondaryButton className="small-banner-btn" onClick={() => onClickLogin(true)}>{t("common.login")}</SecondaryButton>
                      </Stack>
                  </Typography>
              } severity={getCredentialStatusAlert().severity} roundWarningIcon/>
          </Stack>
        }
    </>
}
